import { useRef, useState, useEffect, useCallback } from "react";
import { Slider } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import Hls from "hls.js";

/* ─── Touch device detection ─────────────────────────────────────────────── */
const IS_TOUCH =
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function fmt(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function isHlsSrc(src) {
  if (!src || typeof src !== "string") return false;
  return /\.m3u8(\?|$)/i.test(src) || src.includes("application/vnd.apple.mpegurl");
}

function isPortraitOrientation() {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(orientation: portrait)").matches;
}

/**
 * Given a container box and a video's intrinsic size, compute the actual
 * rendered box of the video under object-fit: contain (i.e. the visible
 * pixels, excluding any letterbox/pillarbox space).
 */
function computeContainedBox(containerW, containerH, videoW, videoH) {
  if (!containerW || !containerH || !videoW || !videoH) return null;
  const containerRatio = containerW / containerH;
  const videoRatio = videoW / videoH;
  if (videoRatio > containerRatio) {
    const width = containerW;
    const height = containerW / videoRatio;
    return { width, height };
  } else {
    const height = containerH;
    const width = containerH * videoRatio;
    return { width, height };
  }
}

/* ─── Styles — Green + White Theme ──────────────────────────────────────── */
const NEON = "#22C55E";
const NEON_DIM = "#16A34A";
const DARK_BG = "#ffffff";

const S = {
  container: (isFs, fillScreen, widenPercent = 0) => ({
    position: isFs ? "fixed" : "relative",
    inset: isFs ? 0 : "auto",
    zIndex: isFs ? 9999 : "auto",
    width:
      isFs || !widenPercent || !IS_TOUCH
        ? "100%"
        : `calc(100% + ${widenPercent * 2}%)`,
    marginLeft:
      isFs || !widenPercent || !IS_TOUCH ? 0 : `-${widenPercent}%`,
    // FIX: mirror marginLeft so the "bleed" is symmetric. Without this,
    // flexbox centering in the parent (contentBox) offsets the box using
    // its full margin box, and an asymmetric margin (marginLeft: -6%,
    // marginRight: 0 by default) pushed the video visibly left on mobile.
    marginRight:
      isFs || !widenPercent || !IS_TOUCH ? 0 : `-${widenPercent}%`,
    height: "100%",
    maxWidth: isFs ? "100%" : "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: DARK_BG,
    overflow: "hidden",
    transition: "background 0.3s ease, width 0.2s ease",
    borderRadius: isFs ? 0 : "14px",
    border: "none",
    boxShadow: "none",
    ...(fillScreen && !isFs
      ? { minHeight: "100%", alignSelf: "stretch" }
      : {}),
  }),

  video: (isFs, isPortraitVideo, fillScreen) => {
    let objectFit = "contain";
    if (isPortraitVideo && (isFs || fillScreen)) {
      objectFit = "contain";
    }
    return {
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      objectFit,
      objectPosition: "center",
      display: "block",
    };
  },

  shimmer: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(90deg, #f0fdf4 0%, #dcfce7 30%, #bbf7d0 50%, #dcfce7 70%, #f0fdf4 100%)",
    backgroundSize: "600px 100%",
    animation: "ccvp-shimmer 1.6s ease-in-out infinite",
    zIndex: 3,
  },

  controlsBar: (visible, boxWidth, scale = 1) => ({
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: boxWidth ? `${boxWidth}px` : "100%",
    maxWidth: "100%",
    zIndex: 6,
    display: "flex",
    alignItems: "center",
    gap: Math.round(2 * scale),
    padding: `${Math.round(10 * scale)}px ${Math.round(6 * scale)}px ${Math.round(14 * scale)}px`,
    borderTop: "none",
    boxShadow: "none",
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? "auto" : "none",
    transition: "opacity 0.28s ease",
    minHeight: Math.round(48 * scale),
    boxSizing: "border-box",
  }),

  iconBtn: (size = 40, width) => ({
    background: "none",
    border: "none",
    padding: 0,
    margin: 0,
    width: width ?? size,
    height: size,
    cursor: "pointer",
    color: "#1a1a1a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    flexShrink: 0,
    filter: `drop-shadow(0 0 3px rgba(34,197,94,0.25)) drop-shadow(0 1px 2px rgba(0,0,0,0.15))`,
    outline: "none",
    transition: "filter 0.18s ease, transform 0.15s ease",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  }),

  timeLabel: (scale = 1) => ({
    color: `rgba(22,163,74,1)`,
    fontSize: Math.max(8, Math.round(11 * scale)),
    fontWeight: 700,
    whiteSpace: "nowrap",
    letterSpacing: "0.04em",
    fontFamily: "inherit",
    flexShrink: 0,
    filter: "none",
    lineHeight: 1,
  }),

  volumeWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },

  volumePopup: (visible) => ({
    position: "absolute",
    bottom: "calc(100% + 8px)",
    left: "50%",
    background: "#ffffff",
    borderRadius: 10,
    padding: "12px 0",
    height: 96,
    width: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
    border: "1px solid rgba(34,197,94,0.15)",
    opacity: visible ? 1 : 0,
    transform: `translateX(-50%) ${visible ? "translateY(0)" : "translateY(6px)"}`,
    pointerEvents: visible ? "auto" : "none",
    transition: "opacity 0.18s ease, transform 0.18s ease",
    zIndex: 10,
  }),
};

/* ─── Keyframe injection (once) ─────────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("ccvp-styles")) {
  const el = document.createElement("style");
  el.id = "ccvp-styles";
  el.textContent = `
    @keyframes ccvp-shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position:  600px 0; }
    }
    .ccvp-icon-btn:hover {
      filter: drop-shadow(0 0 8px rgba(34,197,94,0.70)) drop-shadow(0 1px 3px rgba(0,0,0,0.7)) !important;
      transform: scale(1.12) !important;
    }
    .ccvp-icon-btn:active {
      transform: scale(0.94) !important;
    }
  `;
  document.head.appendChild(el);
}

/**
 * Shared white-label eCheck video player.
 * Supports progressive MP4 and HLS (.m3u8) via hls.js (native HLS on Safari).
 */
export default function EcheckVideoPlayer({
  src,
  videoRef,
  animateIn,
  artifactKey,
  holdAtEnd = false,
  onEnded,
  onTimeUpdate,
  onProgress,
  onReady,
  onDuration,
  autoPlay = true,
  fillScreen = false,
  widenPercent = 6,
}) {
  const containerRef = useRef(null);
  const internalRef = useRef(null);
  const ref = videoRef || internalRef;
  const hlsRef = useRef(null);
  const hideTimer = useRef(null);
  const volumeHideTimer = useRef(null);
  const orientationFsRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const lastVolume = useRef(0.8);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [showVolumePopup, setShowVolumePopup] = useState(false);
  const [isPortraitVideo, setIsPortraitVideo] = useState(false);
  const [videoBoxWidth, setVideoBoxWidth] = useState(null);
  const videoIntrinsic = useRef({ w: 0, h: 0 });

  /* ── Reset on new src ── */
  useEffect(() => {
    setIsLoaded(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setIsPortraitVideo(false);
    setControlsVisible(true);
  }, [src]);

  /* ── Attach HLS or progressive source ── */
  useEffect(() => {
    const v = ref?.current;
    if (!v || !src) return;

    let destroyed = false;

    const cleanupHls = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };

    cleanupHls();

    if (isHlsSrc(src)) {
      const canNativeHls =
        v.canPlayType("application/vnd.apple.mpegurl") ||
        v.canPlayType("application/x-mpegURL");

      if (canNativeHls) {
        v.src = src;
      } else if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          startLevel: -1, // auto adaptive quality
        });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(v);
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (destroyed) return;
          if (data?.fatal) {
            console.error("HLS fatal error", data);
          }
        });
      } else {
        v.src = src;
      }
    } else {
      v.src = src;
    }

    return () => {
      destroyed = true;
      cleanupHls();
      if (v) {
        v.removeAttribute("src");
        v.load();
      }
    };
  }, [ref, src]);

  /* ── holdAtEnd: freeze at last frame ──
   * Important: only *snap* currentTime forward to `duration` when we're
   * already essentially there. `duration` can be a stale, too-short
   * reading right after `loadeddata`/`canplay` fire — especially on iOS
   * Safari, or with any progressively-streamed source where the browser
   * hasn't buffered the whole file yet. If we blindly jump to `duration`
   * whenever we're *not* close to it, we skip over real, unplayed content
   * (this was the cause of the avatar audio audibly cutting out mid-
   * sentence). If `duration` turns out to be wrong, the right move is to
   * just pause where we are — not seek.
   */
  useEffect(() => {
    const v = ref?.current;
    if (!v || !holdAtEnd) return;
    const freeze = () => {
      if (!v.duration || !isFinite(v.duration)) return;

      const gap = v.duration - v.currentTime;

      // Only actually hold/pause when we're genuinely at (or basically
      // at) the end. If the gap is larger than that, the user has
      // rewound to replay the clip — `holdAtEnd` exists to keep the
      // avatar parked on its last frame once it's truly finished, not
      // to block normal playback from an earlier point. This also
      // guards against a race with the parent's `holdAtEnd` prop update:
      // even if this listener was attached back when `holdAtEnd` was
      // still true and hasn't been detached yet, it won't fight a
      // legitimate replay.
      if (gap > 0.5) return;

      v.currentTime = v.duration;
      v.pause();
    };
    let tid = null;
    const stopPlay = () => {
      if (tid) clearTimeout(tid);
      tid = setTimeout(() => {
        tid = null;
        v.pause();
        freeze();
      }, 0);
    };
    v.addEventListener("loadeddata", freeze);
    v.addEventListener("canplay", freeze);
    v.addEventListener("play", stopPlay);
    freeze();
    return () => {
      if (tid) clearTimeout(tid);
      v.removeEventListener("loadeddata", freeze);
      v.removeEventListener("canplay", freeze);
      v.removeEventListener("play", stopPlay);
    };
  }, [ref, holdAtEnd]);

  /* ── Core video events ── */
  useEffect(() => {
    const v = ref?.current;
    if (!v) return;
    if (typeof v.volume === "number") {
      v.volume = volume;
    }

    const onMeta = () => {
      setDuration(v.duration);
      onDuration?.(v.duration);
      if (v.videoHeight > v.videoWidth) {
        setIsPortraitVideo(true);
      } else {
        setIsPortraitVideo(false);
      }
      videoIntrinsic.current = { w: v.videoWidth, h: v.videoHeight };
      if (IS_TOUCH) {
        const c = containerRef.current;
        if (c) {
          const box = computeContainedBox(
            c.clientWidth,
            c.clientHeight,
            v.videoWidth,
            v.videoHeight,
          );
          if (box) setVideoBoxWidth(box.width);
        }
      }
    };
    const onCanPlay = () => {
      setIsLoaded(true);
      onReady?.();
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => {
      if (!isDragging && v.duration && isFinite(v.duration)) {
        const pct = (v.currentTime / v.duration) * 100;
        setProgress(pct);
        setCurrentTime(v.currentTime);
        onTimeUpdate?.(v.currentTime, v.duration);
        onProgress?.(pct, v.currentTime, v.duration);
      }
    };
    const onEnd = () => {
      setIsPlaying(false);
      onEnded?.();
    };
    // `duration` can be revised upward after loadedmetadata/loadeddata,
    // once more of the file has been fetched (progressive/HLS sources).
    // Keep local state and any consumer callback in sync so nothing
    // downstream is left working off a stale short value.
    const onDurationChange = () => {
      if (v.duration && isFinite(v.duration)) {
        setDuration(v.duration);
        onDuration?.(v.duration);
      }
    };

    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("ended", onEnd);
    v.addEventListener("durationchange", onDurationChange);
    if (v.readyState >= 3) onCanPlay();
    if (v.readyState >= 1) onMeta();

    return () => {
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("ended", onEnd);
      v.removeEventListener("durationchange", onDurationChange);
    };
  }, [ref, src, isDragging, onEnded, onTimeUpdate, onProgress, onReady, onDuration, volume]);

  /* ── Portrait 9:16: on phone, rotate to landscape → enter fullscreen. ── */
  useEffect(() => {
    if (!isPortraitVideo || !IS_TOUCH) return;

    const handleOrientation = () => {
      const landscape = !isPortraitOrientation();
      if (landscape) {
        orientationFsRef.current = true;
        setIsFullscreen(true);
      } else if (orientationFsRef.current) {
        orientationFsRef.current = false;
        setIsFullscreen(false);
      }
    };

    const mql = window.matchMedia("(orientation: portrait)");
    const listener = () => handleOrientation();
    if (mql.addEventListener) mql.addEventListener("change", listener);
    else mql.addListener(listener);
    window.addEventListener("orientationchange", handleOrientation);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", listener);
      else mql.removeListener(listener);
      window.removeEventListener("orientationchange", handleOrientation);
    };
  }, [isPortraitVideo]);

  /* ── Track container size changes ── */
  useEffect(() => {
    if (!IS_TOUCH) return;
    const c = containerRef.current;
    if (!c) return;

    const recompute = () => {
      const { w, h } = videoIntrinsic.current;
      if (!w || !h) return;
      const box = computeContainedBox(c.clientWidth, c.clientHeight, w, h);
      if (box) setVideoBoxWidth(box.width);
    };

    recompute();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(recompute);
      ro.observe(c);
    } else {
      window.addEventListener("resize", recompute);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", recompute);
    };
  }, [isFullscreen, isPortraitVideo, fillScreen]);

  /* ── Body scroll lock ── */
  useEffect(() => {
    document.body.style.overflow = isFullscreen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  /* ── Controls visibility ── */
  const scheduleHide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (!isDragging) setControlsVisible(false);
    }, 3000);
  }, [isDragging]);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (isPlaying) scheduleHide();
  }, [isPlaying, scheduleHide]);

  useEffect(() => {
    if (isPlaying && !isDragging) {
      scheduleHide();
    } else {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      setControlsVisible(true);
    }
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [isPlaying, isDragging, scheduleHide]);

  const togglePlayPause = useCallback(() => {
    const v = ref?.current;
    if (!v) return;
    if (v.paused) {
      const playPromise = v.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else {
      v.pause();
    }
    showControls();
  }, [ref, showControls]);

const handleVideoTap = useCallback(
  (e) => {
    if (IS_TOUCH) {
      e.stopPropagation();
      const v = ref?.current;
      const wasPaused = v?.paused;

      togglePlayPause();

      if (wasPaused) {
        // Just started playing — show controls, let them auto-hide
        setControlsVisible(true);
        scheduleHide();
      } else {
        // Just paused — always show controls (no auto-hide while paused)
        if (hideTimer.current) clearTimeout(hideTimer.current);
        setControlsVisible(true);
      }
    } else {
      togglePlayPause();
    }
  },
  [ref, togglePlayPause, scheduleHide],
);

  const toggleMute = useCallback(() => {
    const v = ref?.current;
    if (!v) return;
    if (v.muted || volume === 0) {
      const restored = lastVolume.current > 0 ? lastVolume.current : 0.8;
      v.muted = false;
      v.volume = restored;
      setVolume(restored);
      setIsMuted(false);
    } else {
      lastVolume.current = volume;
      v.muted = true;
      setIsMuted(true);
    }
  }, [ref, volume]);

  const handleVolumeChange = useCallback(
    (_, value) => {
      const v = ref?.current;
      if (!v) return;
      const vol = value / 100;
      v.volume = vol;
      v.muted = vol === 0;
      if (vol > 0) lastVolume.current = vol;
      setVolume(vol);
      setIsMuted(vol === 0);
    },
    [ref],
  );

  const openVolumePopup = useCallback(() => {
    if (volumeHideTimer.current) clearTimeout(volumeHideTimer.current);
    setShowVolumePopup(true);
  }, []);

  const scheduleCloseVolumePopup = useCallback(() => {
    if (volumeHideTimer.current) clearTimeout(volumeHideTimer.current);
    volumeHideTimer.current = setTimeout(() => setShowVolumePopup(false), 350);
  }, []);

  useEffect(() => {
    return () => {
      if (volumeHideTimer.current) clearTimeout(volumeHideTimer.current);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    orientationFsRef.current = false;
    setIsFullscreen((prev) => !prev);
  }, []);

  const handleProgressChange = useCallback(
    (_, value) => {
      setProgress(value);
      const v = ref?.current;
      if (!isDragging && v && v.duration) v.currentTime = (value / 100) * v.duration;
    },
    [ref, isDragging],
  );

  const handleProgressChangeCommitted = useCallback(
    (_, value) => {
      setIsDragging(false);
      const v = ref?.current;
      if (v && v.duration) v.currentTime = (value / 100) * v.duration;
      if (isPlaying) scheduleHide();
    },
    [ref, isPlaying, scheduleHide],
  );

  const shouldFill =
    fillScreen || (isPortraitVideo && isPortraitOrientation() && !isFullscreen);

  const mobileScale =
    IS_TOUCH && videoBoxWidth
      ? Math.min(1, Math.max(0.6, videoBoxWidth / 380))
      : 1;
  const iconBaseSize = IS_TOUCH ? 59 : 40;
  const iconBaseFontSize = IS_TOUCH ? 38 : 22;
  const iconSize = Math.round(iconBaseSize * mobileScale);
  const iconFontSize = Math.round(iconBaseFontSize * mobileScale);
  // Was 1.35 — that extra invisible tap-target padding around each button
  // was eating into the row width the slider could use. 1.1 keeps a
  // slightly wider tap target than the icon itself without costing much
  // horizontal space.
  const iconBtnWidth = IS_TOUCH ? Math.round(iconSize * 1.1) : iconSize;

  // Volume no longer needs a separate rightward shift — tightening the
  // button paddings above already closes the gap to the fullscreen button.
  const VOLUME_SHIFT_PX = 0;

  return (
    <div
      ref={containerRef}
      style={S.container(isFullscreen, shouldFill, widenPercent)}
      onMouseMove={!IS_TOUCH ? showControls : undefined}
      onMouseLeave={
        !IS_TOUCH
          ? () => {
              if (isPlaying) setControlsVisible(false);
            }
          : undefined
      }
    >
      <video
        playsInline
        ref={ref}
        key={artifactKey != null ? `artifact-${artifactKey}-${src}` : undefined}
        autoPlay={autoPlay && !holdAtEnd}
        controls={false}
        preload="auto"
        onClick={handleVideoTap}
        style={{
          ...S.video(isFullscreen, isPortraitVideo, shouldFill),
          opacity: isLoaded ? 1 : 0,
          transition: "opacity 0.4s ease",
        }}
      />

      {!isLoaded && <div style={S.shimmer} />}

      <div
        style={S.controlsBar(controlsVisible, IS_TOUCH ? videoBoxWidth : null, mobileScale)}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          style={S.iconBtn(iconSize, iconBtnWidth)}
          className="ccvp-icon-btn"
          onClick={togglePlayPause}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <PauseIcon style={{ fontSize: iconFontSize }} />
          ) : (
            <PlayArrowIcon style={{ fontSize: iconFontSize }} />
          )}
        </button>

        <span style={S.timeLabel(mobileScale)}>{fmt(currentTime)}</span>

        <Slider
          value={progress}
          onChange={handleProgressChange}
          onChangeCommitted={handleProgressChangeCommitted}
          onMouseDown={() => setIsDragging(true)}
          onTouchStart={() => setIsDragging(true)}
          min={0}
          max={100}
          size="small"
          sx={{
            flex: 1,
            color: NEON,
            padding: `${Math.round(8 * mobileScale)}px ${Math.round(4 * mobileScale)}px ${Math.round(8 * mobileScale)}px ${Math.round(6 * mobileScale)}px`,
            marginRight: `${Math.round(6 * mobileScale)}px`,
            "& .MuiSlider-thumb": {
              width: Math.max(8, Math.round((IS_TOUCH ? 14 : 11) * mobileScale)),
              height: Math.max(8, Math.round((IS_TOUCH ? 14 : 11) * mobileScale)),
              background: `linear-gradient(135deg, ${NEON} 0%, ${NEON_DIM} 100%)`,
              boxShadow: `0 0 0 2px rgba(34,197,94,0.30)`,
              transition: "box-shadow 0.18s ease, transform 0.15s ease",
              "&:hover": {
                boxShadow: `0 0 0 3px rgba(34,197,94,0.40)`,
                transform: "scale(1.15)",
              },
              "&::after": { display: "none" },
              "&.Mui-focusVisible": {
                boxShadow: `0 0 0 3px rgba(34,197,94,0.35)`,
              },
            },
            "& .MuiSlider-track": {
              height: Math.max(2, Math.round(3 * mobileScale)),
              background: `linear-gradient(90deg, ${NEON_DIM} 0%, ${NEON} 100%)`,
              border: "none",
              boxShadow: `0 0 4px rgba(34,197,94,0.40)`,
            },
            "& .MuiSlider-rail": {
              height: Math.max(2, Math.round(3 * mobileScale)),
              opacity: 0.25,
              backgroundColor: "#22C55E",
            },
          }}
        />

        <span style={{ ...S.timeLabel(mobileScale), marginLeft: Math.round(4 * mobileScale) }}>{fmt(duration)}</span>

        <div
          style={{
            ...S.volumeWrap,
            // Shift the volume control (icon + hover popup wrapper) to the
            // right, closer to the fullscreen button.
            marginLeft: VOLUME_SHIFT_PX,
          }}
          onMouseEnter={!IS_TOUCH ? openVolumePopup : undefined}
          onMouseLeave={!IS_TOUCH ? scheduleCloseVolumePopup : undefined}
        >
          {!IS_TOUCH && (
            <div
              style={S.volumePopup(showVolumePopup)}
              onMouseEnter={openVolumePopup}
              onMouseLeave={scheduleCloseVolumePopup}
            >
              <Slider
                value={isMuted ? 0 : Math.round(volume * 100)}
                onChange={handleVolumeChange}
                min={0}
                max={100}
                size="small"
                orientation="vertical"
                aria-label="Volume"
                sx={{
                  height: "100%",
                  color: NEON,
                  "& .MuiSlider-thumb": {
                    width: 10,
                    height: 10,
                    background: `linear-gradient(135deg, ${NEON} 0%, ${NEON_DIM} 100%)`,
                    boxShadow: `0 0 0 2px rgba(34,197,94,0.28)`,
                    transition: "box-shadow 0.18s ease, transform 0.15s ease",
                    "&:hover": {
                      boxShadow: `0 0 0 3px rgba(34,197,94,0.38)`,
                      transform: "scale(1.15)",
                    },
                    "&::after": { display: "none" },
                  },
                  "& .MuiSlider-track": {
                    width: 3,
                    background: `linear-gradient(180deg, ${NEON} 0%, ${NEON_DIM} 100%)`,
                    border: "none",
                    boxShadow: `0 0 4px rgba(34,197,94,0.40)`,
                  },
                  "& .MuiSlider-rail": {
                    width: 3,
                    opacity: 0.25,
                    backgroundColor: "#22C55E",
                  },
                }}
              />
            </div>
          )}

          <button
            style={{
              ...S.iconBtn(iconSize, iconBtnWidth),
              marginRight: IS_TOUCH ? -Math.round((iconBtnWidth - iconSize) / 2) : 0,
            }}
            className="ccvp-icon-btn"
            onClick={toggleMute}
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted || volume === 0 ? (
              <VolumeOffIcon style={{ fontSize: iconFontSize }} />
            ) : volume < 0.5 ? (
              <VolumeDownIcon style={{ fontSize: iconFontSize }} />
            ) : (
              <VolumeUpIcon style={{ fontSize: iconFontSize }} />
            )}
          </button>
        </div>

        <button
          style={{
            ...S.iconBtn(iconSize, iconBtnWidth),
            marginLeft: IS_TOUCH ? -Math.round((iconBtnWidth - iconSize) / 2) : 0,
          }}
          className="ccvp-icon-btn"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? (
            <FullscreenExitIcon style={{ fontSize: iconFontSize }} />
          ) : (
            <FullscreenIcon style={{ fontSize: iconFontSize }} />
          )}
        </button>
      </div>
    </div>
  );
}